import React from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  Button,
  Box,
  Group,
  Stack,
  TextInput,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import * as sentry from "@sentry/react";

import { TanstackMantineLink } from "@components/tanstack-mantine-link";

import { useCheckSpringEligibilityMutation } from "src/backendApi/queries/spring-health-queries";

import type { SpringHealthVerifySearchSchema } from "@routes/spring-health/verify";
const Route = getRouteApi("/spring-health/verify");

interface QuickLink {
  title: string;
  search: SpringHealthVerifySearchSchema;
}

export function SpringHealthEligibilityPage() {
  const { t } = useTranslation();

  const quickLinks: QuickLink[] = [
    {
      title: t("springHealth.verify.debugLinks.eligible"),
      search: { spring_guardian_id: "parent0", spring_id: "child0" },
    },
    {
      title: t("springHealth.verify.debugLinks.ineligible"),
      search: { spring_guardian_id: "parent0", spring_id: "child-ineligible" },
    },
    {
      title: t("springHealth.verify.debugLinks.forceError"),
      search: { spring_guardian_id: "ERR", spring_id: "ERR" },
    },
  ];

  const navigate = Route.useNavigate();

  const searchParams = Route.useSearch();
  const { spring_guardian_id: guardianId, spring_id: springId } = searchParams;

  const { mutate, isPending } = useCheckSpringEligibilityMutation({
    onSuccess: (resp) => {
      const { is_eligible: isEligible } = resp;
      if (!isEligible) {
        notifications.show({
          title: t("springHealth.verify.submitNotifications.notEligibleTitle"),
          message: t("springHealth.verify.submitNotifications.notEligibleBody"),
          color: "yellow",
        });
      } else {
        navigate({
          from: "/spring-health/verify",
          to: "/spring-health/register",
          search: searchParams,
        });
      }
    },
    onError: (err) => {
      sentry.captureException(err);
      notifications.show({
        title: t("springHealth.verify.submitNotifications.errorTitle"),
        message: t("springHealth.verify.submitNotifications.errorBody"),
        color: "red",
      });
    },
  });

  return (
    <Stack>
      <Box>
        <Title order={3}>
          {t("springHealth.verify.marketingPlaceholder.header")}
        </Title>
        <Text>{t("springHealth.verify.marketingPlaceholder.paragraph")}</Text>
      </Box>

      <Box>
        <Text>{t("springHealth.verify.debugLinks.title")}</Text>
        <Group>
          {quickLinks.map((ql, idx) => {
            return (
              <TanstackMantineLink
                key={idx}
                to="/spring-health/verify"
                search={ql.search}
              >
                {ql.title}
              </TanstackMantineLink>
            );
          })}
        </Group>
      </Box>

      <Stack align="flex-start" gap="sm">
        <Box>
          <Title order={3}>{t("springHealth.verify.pageTitle")}</Title>
          <Text>{t("springHealth.verify.pageDescription")}</Text>
        </Box>

        <TextInput
          label={t("springHealth.verify.springId")}
          value={springId}
          disabled
        />
        <TextInput
          label={t("springHealth.verify.guardianId")}
          value={guardianId}
          disabled
        />

        <Button
          loading={isPending}
          onClick={() => {
            mutate({
              payload: {
                spring_guardian_id: guardianId,
                spring_id: springId,
              },
            });
          }}
        >
          {t("springHealth.verify.button")}
        </Button>
      </Stack>
    </Stack>
  );
}
